<template>
  <div
    v-show="$store.state.audioPlayerVisibility"
    class="aplayers-root"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
    @keydown.left.prevent="goPrev"
    @keydown.right.prevent="goNext"
    @keydown.space.prevent="togglePlay"
    tabindex="0"
  >
    <div class="aplayers-topbar">
      <button class="aplayers-back" @click="close">
        <svg viewBox="0 0 24 24" style="min-width: 24px;" fill="none" stroke="currentColor" stroke-width="2.5" width="20" height="20">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <div class="aplayers-title">
        <span class="aplayers-playlist-name">{{ playlistName }}</span>
        <span class="aplayers-index">{{ currentIndex + 1 }} / {{ audioList.length }}</span>
      </div>
      <div style="width:40px"></div>
    </div>

    <Comments style="z-index: 2000;position: absolute" ref="comments" />
    <PlaylistChooser style="z-index: 2000;position: absolute;bottom: 0%;left:0%" ref="playlistChooser" />
    <div v-if="displayCopied" class="aplayers-copied">Link successfully copied</div>

      <AudioPlayer
        v-if="currentPub"
        :infos="currentPub"
        ref="player"
        @publisher-click="onPublisher"
        @comment="onComment"
        @share="onShare"
        @like="onLike"
        @played="onPlayed"
        @addToPlaylist="$refs.playlistChooser.show(currentPub)"
        @ended="goNext"
        @next="goNext"
        @prev="goPrev"
      />

    <Profile :visible="false" ref="profile" />
  </div>
</template>

<script>
import AudioPlayer     from './AudioPlayer.vue'
import Comments        from '@/views/publications/Comments.vue'
import PlaylistChooser from '@/views/components/playlistChooser/PlaylistChooser.vue'
import Profile         from '@/views/profile/Profile.vue'
import { isNativeAudio, NativeAudio } from '@/plugin/nativeAudio'

export default {
  name: 'AudioPlayers',
  components: { AudioPlayer, Comments, PlaylistChooser, Profile },

  data() {
    return {
      audioList:     [],
      currentIndex:  0,
      playlistName:  '',
      displayCopied: false,
      navTimer:     null,
      touchStartX: 0,
      touchStartY: 0,
      ghostPrev: null,
      ghostNext: null
    }
  },

  computed: {
    currentPub() {
      return this.audioList[this.currentIndex] || null
    },
  },

  mounted() {
    window.addEventListener('media:next', this.handleMediaNext)
    window.addEventListener('media:prev', this.handleMediaPrev)
    window.addEventListener('media:track', this.handleMediaTrack)
    window.addEventListener('native:track-changed', this.handleNativeTrackChanged)
  },

  unmounted() {
    window.removeEventListener('media:next', this.handleMediaNext)
    window.removeEventListener('media:prev', this.handleMediaPrev)
    window.removeEventListener('media:track', this.handleMediaTrack)
    window.removeEventListener('native:track-changed', this.handleNativeTrackChanged)
  },

  methods: {
    handleMediaNext() {
      if (this.$store.state.audioPlayerVisibility) {
        this.goNext()
      }
    },
    handleMediaPrev() {
      if (this.$store.state.audioPlayerVisibility) {
        this.goPrev()
      }
    },
    syncNativePlaylist() {
      if (!isNativeAudio) return
      const base = this.$store.state.baseUrl
      const items = this.audioList
        .filter(p => p?.audio)
        .map(p => ({
          id: p.audio,
          url: base + 'audios/' + p.audio,
          title: p.text || 'Audio Track',
          artist: this.$store.state.users[p.userId]?.pseudo || '',
          cover: p.imgMini ? base + 'images/' + p.imgMini : ''
        }))
      NativeAudio.setQueue('playlist', items, this.currentIndex)
    },
    handleNativeTrackChanged(e) {
      if (!NativeAudio.ownsQueue('playlist')) return
      const audioId = e.detail?.id
      if (!audioId || audioId === this._lastHandledTrackId) return
      this._lastHandledTrackId = audioId
      const foundIndex = this.audioList.findIndex(p => p.audio === audioId)
      if (foundIndex < 0 || foundIndex === this.currentIndex) return
      this.currentIndex = foundIndex
      this.$store.commit('player/setCurrentIndex', foundIndex)
      this.$nextTick(() => {
        this.prepareGhosts()
        if (this.$refs.player && typeof this.$refs.player.startPlay === 'function') {
          const pub = this.audioList[foundIndex]
          const ghost = pub?.audio ? this.getGhostFor(pub.audio) : null
          this.$refs.player.startPlay(ghost)
        }
      })
    },
    // ── Entry point called by PlayLists.vue ───────────────────────────────────
    show(audios, startIndex = 0, name = 'Playlist') {
      if(this.audioList.length === 0 && audios.length === 0) return
      this.$store.commit('setCurrentPlayer', 'audioPlayer')
      if(startIndex === null || ( this.currentIndex === startIndex && this.playlistName === name)) {
        this.$store.commit('setAudioPlayerVisibility',true)
        this.syncNativePlaylist()
        return
      }
      this.audioList    = audios
      this.currentIndex = startIndex
      this.playlistName = name
      this.syncNativePlaylist()
      this.$store.commit('setAudioPlayerVisibility',true)
      this.$nextTick(() => {
        this.$el && this.$el.focus()
        this.$refs.player && this.$refs.player.startPlay()
        this.prepareGhosts()
      })
    },

    close() {
    /*   this.$refs.player && this.$refs.player.stopPlay()
      this.$store.commit('setAudioPlayerIsPlaying', false) */
      this.$store.commit('setAudioPlayerVisibility',false)
    },

    // ── Navigation — debounced last-wins ──────────────────────────────────────
    goNext() {
      if (this.currentIndex >= this.audioList.length - 1) return

      this.currentIndex++
      this.$store.commit('player/setCurrentIndex', this.currentIndex)
      this.syncNativePlaylist()
      this._schedulePlay()
      this.$nextTick(() => {
        const pub = this.audioList[this.currentIndex]
        if (pub?.audio) {
          const base = this.$store.state.baseUrl
          NativeAudio.playId(pub.audio, 0, base + 'audios/' + pub.audio, pub.text || '', '', '')
        }
        this.prepareGhosts()
      })
    },

    goPrev() {
      if (this.currentIndex <= 0) return

      this.currentIndex--
      this.$store.commit('player/setCurrentIndex', this.currentIndex)
      this.syncNativePlaylist()
      this._schedulePlay()
      this.$nextTick(() => {
        const pub = this.audioList[this.currentIndex]
        if (pub?.audio) {
          const base = this.$store.state.baseUrl
          NativeAudio.playId(pub.audio, 0, base + 'audios/' + pub.audio, pub.text || '', '', '')
        }
        this.prepareGhosts()
      })
    },/*
    goNext() {
      // prevent when editing
  /*     const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return */
/*
      if (this.currentIndex >= this.audioList.length - 1) return
      this.currentIndex++
      this._schedulePlay()
    },*/

  //  goPrev() {
      // prevent when editing
   /*    const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return */
/*
      if (this.currentIndex <= 0) return
      this.currentIndex--
      this._schedulePlay()
    },*/
    togglePlay() {
      // prevent when editing
      //const tag = document.activeElement?.tagName
      //if (tag === 'INPUT' || tag === 'TEXTAREA') return

      this.$refs.player && this.$refs.player.togglePlay()
    },

    /* _schedulePlay() {
      // Stop immediately so the current Audio element is destroyed right away
      this.$refs.player && this.$refs.player.stopPlay()
      // Cancel any previously scheduled play (absorbs rapid taps)
      clearTimeout(this.navTimer)
      // After a short settle, start the new track
      this.navTimer = setTimeout(() => {
        this.$refs.player && this.$refs.player.startPlay()
      }, 80)
    }, */
    _schedulePlay() {
      this.$refs.player && this.$refs.player.stopPlay()
      clearTimeout(this._navTimer)
      this._navTimer = setTimeout(async () => {
        await this.$nextTick()
        if (!this.$refs.player) return
        const pub   = this.audioList[this.currentIndex]
        const ghost = pub?.audio ? this.getGhostFor(pub.audio) : null
        this.$refs.player.startPlay(ghost)   // ← pass ghost
      }, 80)
    },
    getGhostFor(audioFilename) {
      if (this.ghostPrev && this.ghostPrev.src.includes(audioFilename)) {
        const g = this.ghostPrev
        this.ghostPrev = null   // transfer ownership
        return g
      }
      if (this.ghostNext && this.ghostNext.src.includes(audioFilename)) {
        const g = this.ghostNext
        this.ghostNext = null   // transfer ownership
        return g
      }
      return null
    },
    prepareGhosts() {
      const base = this.$store.state.baseUrl + 'audios/'
      const prev = this.audioList[this.currentIndex + 1]  // +1 because reversed order
      const next = this.audioList[this.currentIndex - 1]  // -1 because reversed order

      if (isNativeAudio) {
        const items = [next, prev].filter(p => p?.audio)
          .map(p => ({ id: p.audio, url: base + p.audio }))
        NativeAudio.preload(items)
      }

      [
        { pub: prev, key: 'ghostPrev' },
        { pub: next, key: 'ghostNext' },
      ].forEach(({ pub, key }) => {
        if (isNativeAudio) return   // skip HTML ghost entirely on Android
        if (!pub?.audio) return
        const existing = this[key]
        if (existing && existing.src.includes(pub.audio)) return  // already preloading
        if (existing) existing.src = ''
        const ghost           = new Audio()
        ghost.crossOrigin     = 'anonymous'
        ghost.preload         = 'auto'
        ghost.src             = base + pub.audio
        this[key]             = ghost

        // Warm image cache too
        ;[pub.img, pub.imgMini].forEach(imgPath => {
          if (!imgPath) return
          const img = new Image()
          img.src   = this.$store.state.baseUrl + 'images/' + imgPath
        })
      })
    },
    
    // ── Swipe horizontal ──────────────────────────────────────────────────────
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchStartY = e.touches[0].clientY
    },
    onTouchEnd(e) {
      const dx = this.touchStartX - e.changedTouches[0].clientX
      const dy = Math.abs(this.touchStartY - e.changedTouches[0].clientY)
      if (Math.abs(dx) < 50 || dy > Math.abs(dx)) return
      dx > 0 ? this.goNext() : this.goPrev()
    },

    // ── Actions ───────────────────────────────────────────────────────────────
    onPublisher(userId) { this.$refs.profile.show({ uid: userId }) },
    onComment(pub)      { this.$refs.comments.show(pub) },
    onPlayed()          { this.setAsViewed() },

    async onShare() {
      const pub = this.currentPub
      if (!pub) return
      const url = this.$store.state.baseUrl + 'publication/' + pub._id
      if (navigator.share) {
        try { await navigator.share({ title: pub.text || '', url }); return }
        catch (e) { if (e.name === 'AbortError') return }
      }
      try {
        await navigator.clipboard.writeText(url)
        this.displayCopied = true
        setTimeout(() => { this.displayCopied = false }, 2000)
      } catch (e) { console.error(e) }
    },

    async onLike() {
      const pub    = this.currentPub
      const player = this.$refs.player
      if (!pub || !player) return
      const isLiked = player.isLiked.value
      const userId  = this.$store.state.user.uid
      try {
        await this.$axios.post('/publication.php', {
          type: isLiked ? 'unlike' : 'like',
          publicationId: pub._id
        })
        if (isLiked) pub.likes = pub.likes.filter(lk => lk.userId !== userId)
        else         pub.likes.push({ userId, createdAt: new Date() })
        player.isLiked.value = !isLiked
      } catch (e) { console.error(e) }
    },

    async setAsViewed() {
      const pub    = this.currentPub
      const userId = this.$store.state.user.uid
      if (!pub) return
      const already = (pub.views || []).find(v => v.userId === userId)
      if (already) return
      try {
        await this.$axios.post('/publication.php', { type: 'see', publicationId: pub._id })
        pub.views = pub.views || []
        pub.views.push({ userId, createdAt: new Date() })
      } catch (e) { console.error(e) }
    },
  },

  beforeUnmount() {
    clearTimeout(this.navTimer)
    if (this.ghostPrev) { this.ghostPrev.src = ''; this.ghostPrev = null }
    if (this.ghostNext) { this.ghostNext.src = ''; this.ghostNext = null }
  },
}
</script>

<style scoped>
.aplayers-root {
  position: fixed;
  top:0px;
  left:0px;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: #0a0e26;
  display: flex;
  flex-direction: column;
  outline: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.aplayers-topbar {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(10,14,38,0.95);
  border-bottom: 1px solid rgba(0,212,255,0.12);
  flex-shrink: 0;
}

.aplayers-back {
  width: 40px; height: 40px;
  background: none; border: none;
  color: #e0f7ff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px;
}
.aplayers-back:hover { background: rgba(0,212,255,0.1); }

.aplayers-title {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.aplayers-playlist-name {
  color: #e0f7ff;
  font-size: 15px; font-weight: 700;
  max-width: 200px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.aplayers-index {
  color: #94a3b8;
  font-size: 11px;
  margin-top: 2px;
  font-family: 'Space Mono', monospace;
}

.aplayers-copied {
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  z-index: 4000;
  padding: 8px 20px;
  background: rgba(10,14,38,0.95);
  border: 1px solid rgba(0,212,255,0.2);
  color: #e0f7ff;
  font-size: 13px;
  border-radius: 8px;
  pointer-events: none;
  white-space: nowrap;
}
</style>